import { Injectable, Optional } from '@angular/core';
import {
    DateRange,
    DisplayNotice,
    mutateState,
    NoticesService,
    patchableState,
    setState,
    Site,
    StateUpdateCommand
} from '@flyfreely-portal-ui/flyfreely';
import { OperationsSearchDataService } from '@flyfreely-portal-ui/operations-shared';
import { SitesDataService } from '@flyfreely-portal-ui/sites-shared';
import { deepEqual } from 'fast-equals';
import {
    combineLatest,
    distinctUntilChanged,
    EMPTY,
    expand,
    filter,
    map,
    of,
    startWith,
    Subject,
    takeUntil
} from 'rxjs';

export interface SimplifiedLotamDetails {
    lotamId: number;
    dateRange: DateRange;
    name: string;
}

/**
 * Provides a shareable service for holding notice search results. If available,
 * it will use the sites data service to provide the list of available sites,
 * otherwise it will default to not using site lists.
 */
@Injectable()
export class MissionNoticesDataService {
    private ngUnsubscribe$ = new Subject<void>();

    private lotamsSource = new Subject<StateUpdateCommand<DisplayNotice[]>>();
    lotams$ = patchableState(this.lotamsSource, [] as DisplayNotice[]);

    constructor(
        operationsSearchDataService: OperationsSearchDataService,
        private noticesService: NoticesService,
        @Optional() sitesDataService: SitesDataService
    ) {
        combineLatest([
            this.noticesService.change$.pipe(startWith(undefined)),
            sitesDataService != null ? sitesDataService.sites$ : of(undefined),
            operationsSearchDataService.currentSearch$.pipe(
                filter(s => s.organisationId != null),
                distinctUntilChanged(deepEqual)
            )
        ])
            .pipe(
                filter(
                    ([change, sites, search]) =>
                        sites === undefined ||
                        sites.organisationId === search.organisationId
                ),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe(([change, sites, search]) =>
                this.refreshNotices(
                    search.time,
                    search.organisationId!,
                    sites?.siteList
                )
            );
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
        this.lotamsSource.complete();
    }

    resetNotices() {
        this.lotamsSource.next(setState([]));
    }

    private refreshNotices(
        time: DateRange | undefined,
        organisationId: number,
        sites?: Site[]
    ) {
        this.resetNotices();

        const includedSiteIdList =
            sites != null ? sites.filter(s => !s.archived).map(s => s.id) : [];

        this.noticesService
            .findNotices(
                { organisationId, time, includedSiteIdList },
                {
                    page: 0,
                    pageSize: 50
                }
            )
            .pipe(
                expand(response => {
                    const pagination = response.pagination;
                    const pages =
                        Math.ceil(response.count / pagination.pageSize) - 1;
                    return pagination.page < pages
                        ? this.noticesService.findNotices(
                              {
                                  organisationId,
                                  time
                              },
                              {
                                  page: pagination.page + 1,
                                  pageSize: pagination.pageSize
                              }
                          )
                        : EMPTY;
                }),
                map(notices =>
                    notices.results.filter(n => n.displayLocation != null)
                ),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe(notices =>
                this.lotamsSource.next(
                    mutateState(existing => [...existing, ...notices])
                )
            );
    }

    acknowledgeMissionLotam(missionId: number, noticeId: number) {
        return this.noticesService.acknowledgeMissionNotice(
            missionId,
            noticeId
        );
    }
}
